import { FC } from "react";
import { useTranslation } from "react-i18next";
import GenderH from "../../assets/gender_H.svg";
import GenderF from "../../assets/gender_F.svg";
import "./evmcGenderLine.css";
import { convertNumberToString } from "../../shared/utils/numbersUtils";

export interface EvmcGenderData {
  ref: number;
  secondary: number;
}

const getIcon = (gender: "H" | "F") => (gender === "H" ? GenderH : GenderF);

interface EvmcGenderLineProps {
  data: EvmcGenderData;
  gender: "H" | "F";
  refCategory: string;
  secondaryCategory: string;
  minAge: number;
  maxAge: number;
}

const EvmcGenderLine: FC<EvmcGenderLineProps> = ({
  data,
  gender,
  refCategory,
  secondaryCategory,
  minAge,
  maxAge,
}) => {
  const { t, i18n } = useTranslation();

  const isSmartphone = window.innerWidth < 422;
  const minAfterWidth = isSmartphone ? 10 : 5;
  const minBeforeWidth = 10;

  const dataFractionPrecision = 1;

  const difference = Math.abs(
    Math.round((data.ref - data.secondary) * 10) / 10
  );
  const refIsLeft = data.ref > data.secondary;
  const maxValue = Math.max(data.ref, data.secondary);
  const minMax = maxAge - minAge;
  // NOTE SUR LE CALCUL
  // L'idée de la constante diffWidth est de calculer la différence entre les deux valeurs de notre en pourcentage de la différence total
  // entre la valeur la moins élevée et la valeur la plus élevée des deux lignes.
  // Ce pourcentage permet de déduire le pourcentage de la largeur de la ligne à occuper avec la div indiquant la différence.
  // Sauf que la plage de largeur disponible pour indiquer les valeurs n'est pas la totalité de la largeur de la ligne.
  // Il y a une marge minimale à gauche et à droite.
  // On retranche donc à ce 100 la largeur indisponible pour avoir un pourcentage représentant la largeur occupable
  // et ainsi avoir des positions cohérentes des étiquettes entre les deux lignes
  const diffWidth =
    minMax !== 0
      ? Math.round(
          (Math.abs(data.ref - data.secondary) *
            (100 - minAfterWidth - minBeforeWidth)) /
            minMax
        )
      : 0;
  const writeDifferenceOutSide = diffWidth < (isSmartphone ? 20 : 15);
  const isTopValue = maxValue === maxAge;

  const differenceRepresentation = (
    <div
      className={
        writeDifferenceOutSide
          ? isTopValue
            ? "difference-outside-left"
            : "difference-outside-right"
          : "difference-inside"
      }
    >
      <span
        className={`gender-difference-number ${
          writeDifferenceOutSide ? `outside-${gender}` : ""
        }`}
      >
        {convertNumberToString(
          difference,
          i18n,
          dataFractionPrecision,
          dataFractionPrecision
        )}
      </span>
      <span
        className={`gender-difference-number-unit ${
          writeDifferenceOutSide ? `outside-${gender}` : ""
        }`}
      >
        {t("evmc-years-difference_interval", {
          postProcess: "interval",
          count: difference,
        })}
      </span>
    </div>
  );

  const getLabelContent = (ref?: boolean) => (
    <>
      <div className="gender-label-category">
        {t(`evmc-filters-${ref ? refCategory : secondaryCategory}`)}
      </div>
      <div>
        <span className="gender-label-value">
          {convertNumberToString(
            ref ? data.ref : data.secondary,
            i18n,
            dataFractionPrecision,
            dataFractionPrecision
          )}
        </span>
        <span className="gender-label-unit">
          {t("year_unit_interval", {
            postProcess: "interval",
            count: ref ? data.ref : data.secondary,
          })}
        </span>
      </div>
    </>
  );

  const afterPadding = minAfterWidth;
  const beforeWidth = isTopValue
    ? 100 - diffWidth - (minBeforeWidth + afterPadding)
    : 0;
  const afterWidth = isTopValue
    ? 0
    : 100 - diffWidth - (minBeforeWidth + afterPadding);

  const beforeDiff = minBeforeWidth + beforeWidth;
  const afterDiff = isTopValue ? 0 : afterPadding + afterWidth;

  const labelRefBeforeWidth = !refIsLeft
    ? beforeDiff
    : isTopValue
    ? 100 - afterPadding
    : beforeDiff + diffWidth;
  const labelSecBeforeWidth = refIsLeft
    ? beforeDiff
    : isTopValue
    ? 100 - afterPadding
    : beforeDiff + diffWidth;
  const labelRefAfterWidth = !refIsLeft
    ? afterDiff
    : isTopValue
    ? afterPadding
    : afterDiff;
  const labelSecAfterWidth = refIsLeft
    ? afterDiff
    : isTopValue
    ? afterPadding
    : afterDiff;

  return (
    <div className="gender-line-container">
      <div className="gender-icon-container">
        <span>
          <img src={getIcon(gender)} alt={t(`evmc-${gender}-icon-alt`) || ""} />
        </span>
        <span className="gender-icon-text">{t(`evmc-${gender}-gender`)}</span>
      </div>
      <div className="gender-line-data-container">
        <div className="gender-diff-labels-container">
          <div className="gender-label-arrow">
            <span style={{ width: `${labelRefBeforeWidth}%` }}>&nbsp;</span>
            <div className="gender-label-container">
              <div className={`gender-label-${gender}`}>
                {getLabelContent(true)}
              </div>
              <div className={`gender-label-arrow-down-${gender}`} />
            </div>
            <span style={{ width: `${labelRefAfterWidth}%` }}>&nbsp;</span>
          </div>
          <div className="gender-line-arrow-container">
            <div className="gender-line-difference-container">
              <span
                style={{ width: `${10 + beforeWidth}%` }}
                className="difference-before"
              >
                {writeDifferenceOutSide && isTopValue
                  ? differenceRepresentation
                  : undefined}
              </span>
              <div
                className={`gender-difference-representation-${gender}`}
                style={{ width: `${diffWidth}%` }}
              >
                {writeDifferenceOutSide ? undefined : differenceRepresentation}
              </div>
              {writeDifferenceOutSide && !isTopValue
                ? differenceRepresentation
                : undefined}
              <span style={{ width: `${afterPadding}%` }}>&nbsp;</span>
            </div>
          </div>
          <div className="gender-label-arrow">
            <span style={{ width: `${labelSecBeforeWidth}%` }}>&nbsp;</span>
            <div className="gender-label-container">
              <div className={`gender-label-arrow-up-${gender}`} />
              <div className={`gender-label-${gender}`}>
                {getLabelContent(false)}
              </div>
            </div>
            <span style={{ width: `${labelSecAfterWidth}%` }}>&nbsp;</span>
          </div>
        </div>
      </div>
      <div className="gender-line-data-arrow" />
    </div>
  );
};

export default EvmcGenderLine;
